import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Grid,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { validateContent, getContentDetails, stakeForValidation } from '../utils/contracts';
import { ethers } from 'ethers';

function ContentValidation({ wallet, contracts }) {
  const [contents, setContents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isStakeDialogOpen, setStakeDialogOpen] = useState(false);

  const loadContents = async () => {
    // In a real implementation, you would fetch content from an indexer or event logs
    // This is a placeholder for demonstration
    setContents([
      {
        hash: 'ipfs://Qm...',
        type: 'image',
        isAIGenerated: true,
        creator: '0x...',
        validationCount: 3,
      },
      // Add more mock content
    ]);
  };

  useEffect(() => {
    if (wallet && contracts) {
      loadContents();
    }
  }, [wallet, contracts]);

  const handleValidate = async contentHash => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const tx = await validateContent(contracts.contentAuth, contentHash);
      setSuccess(`Content validated successfully! Transaction: ${tx}`);
      loadContents(); // Reload content list
    } catch (error) {
      if (error.message.includes('Not a verified validator')) {
        setStakeDialogOpen(true);
      } else {
        setError(error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleStake = async () => {
    setLoading(true);
    try {
      const minStake = ethers.parseEther('1.0'); // 1 ETH
      const tx = await stakeForValidation(contracts.contentAuth, minStake);
      setSuccess(`Staked successfully! Transaction: ${tx}`);
      setStakeDialogOpen(false);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  if (!wallet) {
    return <Alert severity="info">Please connect your wallet to validate content.</Alert>;
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Content Validation
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        {contents.map(content => (
          <Grid item xs={12} sm={6} md={4} key={content.hash}>
            <Card>
              <CardMedia
                component="img"
                height="200"
                image={`https://ipfs.io/ipfs/${content.hash.replace('ipfs://', '')}`}
                alt="Content preview"
              />
              <CardContent>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Hash: {content.hash.slice(0, 10)}...
                </Typography>
                <Box sx={{ mb: 1 }}>
                  <Chip
                    label={content.isAIGenerated ? 'AI Generated' : 'Human Created'}
                    color={content.isAIGenerated ? 'secondary' : 'primary'}
                    size="small"
                    sx={{ mr: 1 }}
                  />
                  <Chip
                    label={`${content.validationCount} Validations`}
                    variant="outlined"
                    size="small"
                  />
                </Box>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => handleValidate(content.hash)}
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={24} /> : 'Validate'}
                </Button>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Dialog open={isStakeDialogOpen} onClose={() => setStakeDialogOpen(false)}>
        <DialogTitle>Become a Validator</DialogTitle>
        <DialogContent>
          <Typography>
            To validate content, you need to stake 1 ETH. This stake can be withdrawn later if you
            maintain a positive reputation.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStakeDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleStake} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Stake 1 ETH'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ContentValidation;
